import React, { useEffect, useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import SlideOutSidebar from "ui-components/slide-out-sidebar/slideOutSidebar";
import Button, { ButtonThemes } from "ui-components/button/button";
import Table from "ui-components/table/table";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import {
  LoadBoardChangeActionType,
  LoadBoardChangeLogEntry,
  formatActionLabel,
  formatChangeValue,
  formatFieldLabel,
} from "app/data/load-board/loadBoardChangeLogModels";
import { useLoadBoardChangeLog } from "./hooks/useLoadBoardChangeLog";
import "./loadBoardActivityLog.scss";

type LoadBoardActivityLogProps = {
  show: boolean;
  onClose: () => void;
};

type LoadBoardActivityLogRow = {
  changedAt: string;
  modifiedBy: string;
  manifestNumber: string;
  tripNumber: string;
  action: string;
  field: string;
  previousValue: string;
  newValue: string;
  reason: string;
};

const ACTION_TYPE_OPTIONS: XGSSelectOption[] = [
  { label: "All", value: "" },
  { label: "Assign Door", value: "ASSIGN_DOOR" },
  { label: "Dispatch", value: "DISPATCH" },
];

const columnHelper = createColumnHelper<LoadBoardActivityLogRow>();

const columns = [
  columnHelper.accessor("changedAt", {
    id: "changedAt",
    header: "Timestamp",
    cell: (cellProps) => {
      const value = cellProps.getValue();
      return (
        <>
          {value && (
            <div className="xgs-loadboard-activity-log__date">
              <div>{(value as any).toUiDateFormat?.() ?? value}</div>
              <div className="xgs-loadboard-activity-log__gray-value">
                {(value as any).toUiTimeShortFormat?.() ?? ""}
              </div>
            </div>
          )}
        </>
      );
    },
    size: 140,
    minSize: 120,
  }),
  columnHelper.accessor("modifiedBy", {
    id: "modifiedBy",
    header: "User",
    size: 100,
    minSize: 80,
  }),
  columnHelper.accessor("manifestNumber", {
    id: "manifestNumber",
    header: "Manifest",
    size: 100,
    minSize: 80,
  }),
  columnHelper.accessor("tripNumber", {
    id: "tripNumber",
    header: "Stop",
    size: 70,
    minSize: 50,
  }),
  columnHelper.accessor("action", {
    id: "action",
    header: "Action",
    size: 110,
    minSize: 90,
  }),
  columnHelper.accessor("field", {
    id: "field",
    header: "Field",
    size: 120,
    minSize: 90,
  }),
  columnHelper.accessor("previousValue", {
    id: "previousValue",
    header: "Previous",
    size: 140,
    minSize: 100,
  }),
  columnHelper.accessor("newValue", {
    id: "newValue",
    header: "New",
    size: 140,
    minSize: 100,
  }),
  columnHelper.accessor("reason", {
    id: "reason",
    header: "Reason",
    size: 160,
    minSize: 100,
  }),
];

const mapToRows = (items: LoadBoardChangeLogEntry[]): LoadBoardActivityLogRow[] =>
  items.map((item) => ({
    changedAt: item.changedAt,
    modifiedBy: item.modifiedBy || "—",
    manifestNumber: item.manifestNumber != null ? String(item.manifestNumber) : "—",
    tripNumber: item.tripNumber != null ? String(item.tripNumber) : "—",
    action: formatActionLabel(item.actionType),
    field: formatFieldLabel(item.fieldName),
    previousValue: formatChangeValue(item.previousValue, item.fieldName),
    newValue: formatChangeValue(item.newValue, item.fieldName),
    reason: item.reasonDescription || item.reasonCode || "—",
  }));

/**
 * Load Board generic change audit (Activity Log).
 * Separate from Carrier Change Log. GET /load-board/change-log
 */
const LoadBoardActivityLog: React.FC<LoadBoardActivityLogProps> = ({
  show,
  onClose,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const [actionType, setActionType] = useState<XGSSelectOption | null>(
    ACTION_TYPE_OPTIONS[0]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const toApiDate = (value?: string) =>
    value ? (value as any).toApiDateFormat?.() ?? value : undefined;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isError,
  } = useLoadBoardChangeLog(
    {
      search: debouncedSearch || undefined,
      dateFrom: toApiDate(dateFrom),
      dateTo: toApiDate(dateTo),
      actionType: (actionType?.value as LoadBoardChangeActionType) || undefined,
    },
    show
  );

  const rows = useMemo(() => {
    const items = data?.pages.flatMap((page) => page.content) ?? [];
    return mapToRows(items);
  }, [data]);

  const onSearch = () => {
    setDebouncedSearch(searchInput.trim());
  };

  const onClearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setDateFrom(undefined);
    setDateTo(undefined);
    setActionType(ACTION_TYPE_OPTIONS[0]);
  };

  const hasActiveFilters =
    !!searchInput || !!dateFrom || !!dateTo || (!!actionType?.value && actionType.value !== "");

  return (
    <SlideOutSidebar
      header="Activity Log"
      onClose={onClose}
      show={show}
      size="medium"
      className="xgs-loadboard-activity-log"
    >
      <div className="xgs-loadboard-activity-log__filters">
        <LabeledTextInput
          label="Search"
          labelMode={LabelModes.column}
          className="xgs-loadboard-activity-log__filter-input"
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
          type="text"
          placeholder="Manifest, user, field..."
        />
        <LabeledDateRangeInput
          label="Date Range:"
          labelMode={LabelModes.column}
          className="xgs-loadboard-activity-log__filter-dates"
          start={dateFrom}
          end={dateTo}
          maxDate={new Date()}
          onStartChange={(v) => setDateFrom(v || undefined)}
          onEndChange={(v) => setDateTo(v || undefined)}
        />
        <LabeledSelectInput
          label="Action"
          labelMode={LabelModes.column}
          className="xgs-loadboard-activity-log__filter-action"
          options={ACTION_TYPE_OPTIONS}
          value={actionType}
          onValueChange={(opt) => setActionType(opt ?? ACTION_TYPE_OPTIONS[0])}
          placeholder="All"
        />
        <Button
          className="xgs-loadboard-activity-log__filter-button"
          theme={ButtonThemes.gray}
          onClick={onClearFilters}
          disabled={!hasActiveFilters}
        >
          Clear Filters
        </Button>
        <Button
          className="xgs-loadboard-activity-log__filter-button"
          theme={ButtonThemes.blue}
          onClick={onSearch}
        >
          Search
        </Button>
      </div>

      <div className="xgs-loadboard-activity-log__table-wrapper">
        {!isError && (
          <Table
            isLoading={isLoading}
            columns={columns}
            data={rows}
            rowHeight={48}
            minTableHeight={200}
            infiniteScroll={true}
            noResultsText="No changes recorded yet"
            infiniteScrollLoading={isFetchingNextPage}
            infiniteScrollHasNext={!!hasNextPage}
            onInfiniteScroll={() => {
              if (hasNextPage) fetchNextPage();
            }}
          />
        )}
      </div>

      {isError && (
        <XGSErrorMessage className="xgs-loadboard-activity-log__error">
          Error while obtaining Load Board activity log
        </XGSErrorMessage>
      )}
    </SlideOutSidebar>
  );
};

export default LoadBoardActivityLog;
